import React from "react";
import { User } from "../types/user";
import { rememberPerson, findKnownPerson } from "../data/knownPeople";
import { useAuth } from "../data/AuthContext";
import { setMyName, setMyVenmo } from "../data/myProfile";

interface RosterEditorArgs {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  whichUserPaidIndex: number;
  setWhichUserPaidIndex: React.Dispatch<React.SetStateAction<number>>;
}

// Editing mates by row index, shared by the mates table (on the page, before
// the check is shared — no one holds portions yet, so deleting a mate needs no
// item cleanup) and the selected-mate editor on the shared check (edit only,
// no delete). Row 0 is "Me": never deleted, and remembered on this device
// (localStorage) rather than in the saved-mates directory.
export function useRosterEditor({
  users,
  setUsers,
  whichUserPaidIndex,
  setWhichUserPaidIndex,
}: RosterEditorArgs) {
  const { uid } = useAuth();

  // keep the "who paid" pointer on the same person after rows shift
  const repointPaid = (newUsers: User[], paidId: string | undefined) => {
    const newPaidIndex = newUsers.findIndex((u) => u.id === paidId);
    setWhichUserPaidIndex(newPaidIndex === -1 ? 0 : newPaidIndex);
  };

  // live-edit straight on the roster; trimming happens on commit
  const patchUser = (index: number, patch: Partial<User>) => {
    setUsers(users.map((u, i) => (i === index ? { ...u, ...patch } : u)));
  };

  // on blur: normalize and remember to the directory. Mates with the same name
  // + venmo are left as separate people — never merged.
  //
  // knownPeople: pass the saved-mates directory to adopt a remembered person's
  // id when name+venmo match, so typing a saved mate doesn't duplicate them in
  // the directory. Only safe pre-share — an id swap would orphan that user's
  // shares.
  const commitUser = (index: number, knownPeople?: User[]) => {
    const current = users[index];
    if (!current || current.name.trim() === "") return;
    const isMe = index === 0;

    const updated: User = {
      ...current,
      name: current.name.trim(),
      venmo: current.venmo?.trim() || undefined,
    };

    if (knownPeople && !isMe) {
      const known = findKnownPerson(knownPeople, updated.name, updated.venmo);
      if (known && !users.some((u) => u.id === known.id)) {
        updated.id = known.id;
      }
    }

    setUsers(users.map((u, i) => (i === index ? updated : u)));
    if (isMe) {
      // your own row: remembered on this device to prefill the next check
      setMyName(updated.name);
      setMyVenmo(updated.venmo ?? "");
    } else if (uid) {
      rememberPerson(uid, updated);
    }
  };

  const removeUser = (index: number) => {
    if (index === 0 || !users[index]) return;
    const newUsers = users.filter((_, i) => i !== index);
    setUsers(newUsers);
    repointPaid(newUsers, users[whichUserPaidIndex]?.id);
  };

  return { patchUser, commitUser, removeUser };
}
